
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { format, parse } from 'date-fns';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useBirthdays } from '@/hooks/use-birthdays';
import type { Birthday, BirthdayInput } from '@/lib/types';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  dob: z.string().refine((val) => !val || /^\d{2}\/\d{2}\/\d{4}$/.test(val), {
    message: 'Date must be in DD/MM/YYYY format or be empty.',
  }),
  time: z.string().optional(),
  relation: z.string().min(2, 'Relation is required.'),
  notes: z.string().optional(),
});

type BirthdayFormProps = {
  birthday?: Birthday;
  onSuccess: () => void;
};

export function BirthdayForm({ birthday, onSuccess }: BirthdayFormProps) {
  const { addBirthday, updateBirthday } = useBirthdays();
  
  const defaultDob = birthday?.dob ? format(parse(birthday.dob, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy') : '';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: birthday
      ? { ...birthday, dob: defaultDob }
      : {
          name: '',
          dob: '',
          time: '',
          relation: 'Friend',
          notes: '',
        },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    const formattedValues: BirthdayInput = {
      ...values,
      dob: values.dob ? format(parse(values.dob, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd') : '',
    };

    if (birthday) {
      updateBirthday({ ...birthday, ...formattedValues });
    } else {
      addBirthday(formattedValues);
    }

    onSuccess();
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const valueLength = rawValue.length;
    let formattedValue = rawValue;

    if (valueLength > 2) {
      formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2)}`;
    }
    if (valueLength > 4) {
      formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2, 4)}/${rawValue.slice(4, 8)}`;
    }
    
    field.onChange(formattedValue.slice(0, 10));
  };


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Jane Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="dob"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date of Birth</FormLabel>
                <FormControl>
                  <Input 
                    type="text" 
                    placeholder="DD/MM/YYYY" 
                    {...field}
                    onChange={(e) => handleDateChange(e, field)}
                    maxLength={10}
                  />
                </FormControl>
                <FormDescription>
                  Date as DD/MM/YYYY.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
           <FormField
            control={form.control}
            name="time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Time (Optional)</FormLabel>
                <FormControl>
                  <Input 
                    type="time"
                    {...field}
                  />
                </FormControl>
                 <FormDescription>
                  Time of birth.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="relation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Relation</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a relation" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Friend">Friend</SelectItem>
                  <SelectItem value="Family">Family</SelectItem>
                  <SelectItem value="Colleague">Colleague</SelectItem>
                  <SelectItem value="Partner">Partner</SelectItem>
                  <SelectItem value="Sibling">Sibling</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Likes, dislikes, gift ideas..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Add any notes that might be helpful for gift suggestions.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit">{birthday ? 'Save Changes' : 'Add Birthday'}</Button>
      </form>
    </Form>
  );
}
